import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Returns a function whose identity is stable for the component's lifetime
 * but whose body always points at the latest closure. Mirrors React's
 * upcoming `useEffectEvent` semantics, ported manually until it lands.
 *
 * Use anywhere a callback prop ends up in an effect's dep array somewhere
 * downstream. Without this wrapper, an inline arrow at the call site
 * (`<Child onFoo={(x) => doThing(x)} />`) creates a fresh function reference
 * every render, which in turn re-fires every effect that lists `onFoo` as a
 * dep — typically producing a render loop.
 *
 * `useLayoutEffect` (not `useEffect`) so the ref is updated before any
 * child effect that fires this turn reads it.
 */
export function useStableCallback<A extends unknown[], R>(fn: (...args: A) => R) {
    const ref = useRef(fn);
    useLayoutEffect(() => {
        ref.current = fn;
    });
    return useCallback((...args: A) => ref.current(...args), []);
}
